import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Plus, Sparkles, Pencil, Trash2, FileText, ExternalLink, ScanLine, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import CountrySelect, { DEFAULT_COUNTRY_CODE, getCountryBadgeClass, getCountryLabel, normalizeCountryCode } from '../components/CountrySelect';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import FilePreviewModal from '../components/FilePreviewModal';
import { canEditFleetRecord, canHardDelete } from '../utils/permissions';
import { useRecordHighlight } from '../utils/recordHighlight';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCEPTED_FILE_TYPES =
  'image/jpeg,image/png,image/webp,image/gif,application/pdf,' +
  '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,' +
  'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/csv';

const DOCUMENT_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'ROADWORTHY_CERT', label: 'Roadworthy Certificates' },
  { value: 'AMA_STICKER', label: 'AMA Stickers' },
  { value: 'VEHICLE_REGISTRATION', label: 'Vehicle Registration Certificates (VRC)' },
  { value: 'INSURANCE', label: 'Insurance Documents' },
  { value: 'DRIVER_LICENSE', label: "Drivers' Licenses" },
  { value: 'OTHER', label: 'Other Documents' },
];

/** Log implementation details for developers — never show these in the UI. */
const logDocumentsDev = (message, ...args) => {
  console.debug(`[GTI Fleet Documents] ${message}`, ...args);
};

const userFacingOcrMessage = (technicalError) => {
  if (technicalError) {
    logDocumentsDev('OCR unavailable or failed:', technicalError);
  }
  return 'Automatic scanning could not read this document. You can enter the details manually or try another file.';
};

const userFacingApiError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail) {
    logDocumentsDev('API error:', detail);
  }
  if (typeof detail === 'string' && detail.length < 120 && !/EMERGENT|S3_|AWS_|mongodb|traceback/i.test(detail)) {
    return detail;
  }
  return fallback;
};

const createInitialFormData = () => ({
  country: DEFAULT_COUNTRY_CODE,
  document_type: 'ROADWORTHY_CERT',
  entity_id: '',
  entity_type: 'VEHICLE',
  document_number: '',
  issue_date: new Date().toISOString().split('T')[0],
  expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  renewal_date: '',
});

const documentToFormData = (doc) => ({
  country: normalizeCountryCode(doc.country),
  document_type: doc.document_type,
  entity_id: doc.entity_id,
  entity_type: doc.entity_type,
  document_number: doc.document_number,
  issue_date: typeof doc.issue_date === 'string' ? doc.issue_date.split('T')[0] : new Date(doc.issue_date).toISOString().split('T')[0],
  expiry_date: doc.expiry_date
    ? (typeof doc.expiry_date === 'string' ? doc.expiry_date.split('T')[0] : new Date(doc.expiry_date).toISOString().split('T')[0])
    : '',
  renewal_date: doc.renewal_date
    ? (typeof doc.renewal_date === 'string' ? doc.renewal_date.split('T')[0] : new Date(doc.renewal_date).toISOString().split('T')[0])
    : '',
});

const isVrcType = (type) => type === 'VEHICLE_REGISTRATION';

const Documents = () => {
  const { user, token } = useAuth();
  const canEdit = canEditFleetRecord(user?.role);
  const canDelete = canHardDelete(user?.role, 'document');

  const [documents, setDocuments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [runOcrOnUpload, setRunOcrOnUpload] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ocrRunningId, setOcrRunningId] = useState(null);
  const [formData, setFormData] = useState(createInitialFormData);
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const initialTab = searchParams.get('type') || 'ALL';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [entityFilter, setEntityFilter] = useState(searchParams.get('entity_id') || '');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCountry, setBulkCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [bulkMetaFile, setBulkMetaFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sortField, setSortField] = useState('issue_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterEntityName, setFilterEntityName] = useState('');
  const { rowHighlightProps } = useRecordHighlight('document');
  const highlightParam = searchParams.get('highlight') || searchParams.get('entity_id') || '';

  const fetchData = useCallback(async (documentType = activeTab) => {
    try {
      const params = {};
      if (documentType && documentType !== 'ALL') {
        params.document_type = documentType;
      }
      if (entityFilter) {
        params.entity_id = entityFilter;
      }
      const [docsRes, vehiclesRes, driversRes] = await Promise.all([
        axios.get(`${API}/documents`, { params }),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/drivers`),
      ]);
      setDocuments(docsRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [activeTab, entityFilter]);

  const getEntityLabel = useCallback((doc) => {
    const entity = doc.entity_type === 'VEHICLE'
      ? vehicles.find((v) => v.id === doc.entity_id)
      : drivers.find((d) => d.id === doc.entity_id);
    if (doc.entity_type === 'VEHICLE') return entity?.registration_number || '';
    return entity ? `${entity.first_name} ${entity.last_name}` : '';
  }, [vehicles, drivers]);

  const displayedDocuments = useMemo(() => {
    let rows = [...documents];
    const q = filterSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((d) =>
        (d.document_number || '').toLowerCase().includes(q) ||
        getEntityLabel(d).toLowerCase().includes(q)
      );
    }
    if (filterEntityName.trim()) {
      const n = filterEntityName.trim().toLowerCase();
      rows = rows.filter((d) => getEntityLabel(d).toLowerCase().includes(n));
    }
    rows.sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'entity_name') {
        av = getEntityLabel(a);
        bv = getEntityLabel(b);
      }
      if (sortField.includes('date') && av) av = new Date(av).getTime();
      if (sortField.includes('date') && bv) bv = new Date(bv).getTime();
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [documents, filterSearch, filterEntityName, sortField, sortDir, getEntityLabel]);

  useEffect(() => {
    fetchData(activeTab);
    logDocumentsDev(
      'Storage uses S3 when S3_BUCKET_NAME is set on the API; otherwise local disk. OCR requires EMERGENT_LLM_KEY on the backend.'
    );
  }, [fetchData, activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setLoading(true);
  };

  const resetDialogState = () => {
    setEditingId(null);
    setFormData(createInitialFormData());
    setSelectedFile(null);
    setRunOcrOnUpload(true);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) resetDialogState();
  };

  const openCreateDialog = () => {
    resetDialogState();
    setDialogOpen(true);
  };

  const openEditDialog = (doc) => {
    setEditingId(doc.id);
    setFormData(documentToFormData(doc));
    setSelectedFile(null);
    setRunOcrOnUpload(false);
    setDialogOpen(true);
  };

  const uploadFileForDocument = async (documentId, file) => {
    const uploadData = new FormData();
    uploadData.append('file', file);
    await axios.post(`${API}/documents/${documentId}/upload`, uploadData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const runOcrForDocument = async (documentId) => {
    const response = await axios.post(`${API}/documents/${documentId}/ocr`);
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && !selectedFile) {
      toast.error('Please select a document file to upload');
      return;
    }

    const payload = {
      ...formData,
      issue_date: new Date(formData.issue_date).toISOString(),
      expiry_date: formData.expiry_date
        ? new Date(formData.expiry_date).toISOString()
        : null,
      renewal_date: formData.renewal_date
        ? new Date(formData.renewal_date).toISOString()
        : null,
    };

    if (!isVrcType(formData.document_type) && !formData.expiry_date) {
      toast.error('Expiry date is required for this document type');
      return;
    }

    setUploading(true);
    try {
      let documentId = editingId;
      if (editingId) {
        await axios.put(`${API}/documents/${editingId}`, payload);
      } else {
        const createRes = await axios.post(`${API}/documents`, payload);
        documentId = createRes.data.id;
      }

      if (selectedFile) {
        await uploadFileForDocument(documentId, selectedFile);
      }

      if (runOcrOnUpload && (selectedFile || editingId)) {
        try {
          const ocrResult = await runOcrForDocument(documentId);
          if (ocrResult?.error) {
            toast.warning(`Document saved. ${userFacingOcrMessage(ocrResult.error)}`);
          } else {
            toast.success('Document saved and OCR completed');
          }
        } catch {
          toast.warning('Document saved, but OCR could not be completed');
        }
      } else {
        toast.success(editingId ? 'Document updated successfully!' : 'Document added successfully!');
      }

      handleDialogOpenChange(false);
      fetchData();
    } catch (error) {
      toast.error(userFacingApiError(error, editingId ? 'Failed to update document' : 'Failed to add document'));
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = async (doc) => {
    if (!doc.s3_key && !doc.file_url) {
      toast.error('No file uploaded for this document');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/documents/${doc.id}/download-url`);
      const url = data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
      setPreview({
        url,
        filename: doc.original_filename || doc.document_number || 'document',
        contentType: doc.content_type || '',
        title: getDocumentTypeLabel(doc.document_type),
      });
    } catch {
      toast.error('Could not open document');
    }
  };

  const handleRunOcr = async (doc) => {
    setOcrRunningId(doc.id);
    try {
      const ocrResult = await runOcrForDocument(doc.id);
      if (ocrResult?.error) {
        toast.error(userFacingOcrMessage(ocrResult.error));
      } else {
        toast.success('Document details updated from scan');
        fetchData();
      }
    } catch (error) {
      toast.error(userFacingApiError(error, 'Scan could not be completed'));
    } finally {
      setOcrRunningId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${deleteTarget.id}`);
      toast.success('Document deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(userFacingApiError(error, 'Failed to delete document'));
    } finally {
      setDeleting(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      ROADWORTHY_CERT: 'Roadworthy Certificate',
      AMA_STICKER: 'AMA Sticker',
      INSURANCE: 'Insurance',
      DRIVER_LICENSE: 'Driver License',
      VEHICLE_REGISTRATION: 'Vehicle Registration Certificate',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const resetBulkDialog = () => {
    setBulkMetaFile(null);
    setBulkFiles([]);
    setBulkResult(null);
    setBulkCountry(DEFAULT_COUNTRY_CODE);
  };

  const handleBulkDialogOpenChange = (open) => {
    setBulkDialogOpen(open);
    if (!open) resetBulkDialog();
  };

  const downloadBulkTemplate = async () => {
    try {
      const response = await axios.get(`${API}/documents/bulk-upload/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'document-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkMetaFile) {
      toast.error('Please select the Excel metadata file');
      return;
    }
    if (!bulkFiles.length) {
      toast.error('Please select one or more document files');
      return;
    }
    const uploadData = new FormData();
    uploadData.append('country', bulkCountry);
    uploadData.append('metadata', bulkMetaFile);
    bulkFiles.forEach((file) => uploadData.append('files', file));
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const { data } = await axios.post(`${API}/documents/bulk-upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      if (data.created > 0) {
        fetchData();
        toast.success(`${data.created} document${data.created === 1 ? '' : 's'} imported`);
      }
      if (data.failed > 0 && data.created === 0) {
        toast.error('No documents were imported. Review the errors below.');
      } else if (data.failed > 0) {
        toast.warning(`${data.failed} row${data.failed === 1 ? '' : 's'} could not be imported`);
      }
    } catch (error) {
      toast.error(userFacingApiError(error, 'Bulk upload failed'));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="documents-page">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Documents</h1>
          <p className="text-slate-600 mt-1">Manage compliance documents for vehicles and drivers</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
        <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-document-btn" onClick={openCreateDialog}>
              <Plus size={18} className="mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Document' : 'Add Document'}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? 'Update document details or replace the uploaded file.'
                  : 'Add a compliance document for a vehicle or driver.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Country</Label>
                <CountrySelect
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Document Type</Label>
                  <Select value={formData.document_type} onValueChange={(value) => setFormData({
                    ...formData,
                    document_type: value,
                    expiry_date: isVrcType(value) ? '' : (formData.expiry_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
                  })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ROADWORTHY_CERT">Roadworthy Certificate</SelectItem>
                      <SelectItem value="AMA_STICKER">AMA Sticker</SelectItem>
                      <SelectItem value="VEHICLE_REGISTRATION">Vehicle Registration Certificate (VRC)</SelectItem>
                      <SelectItem value="INSURANCE">Insurance Document</SelectItem>
                      <SelectItem value="DRIVER_LICENSE">Driver License</SelectItem>
                      <SelectItem value="OTHER">Other Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entity Type</Label>
                  <Select value={formData.entity_type} onValueChange={(value) => setFormData({ ...formData, entity_type: value, entity_id: '' })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VEHICLE">Vehicle</SelectItem>
                      <SelectItem value="DRIVER">Driver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{formData.entity_type === 'VEHICLE' ? 'Vehicle' : 'Driver'}</Label>
                <Select value={formData.entity_id} onValueChange={(value) => setFormData({ ...formData, entity_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.entity_type === 'VEHICLE'
                      ? vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.registration_number} - {v.make} {v.model}
                          </SelectItem>
                        ))
                      : drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.first_name} {d.last_name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document Number</Label>
                <Input value={formData.document_number} onChange={(e) => setFormData({ ...formData, document_number: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Date</Label>
                  <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} required />
                </div>
                <div>
                  <Label>
                    Expiry Date
                    {isVrcType(formData.document_type) ? ' (optional for VRC)' : ' *'}
                  </Label>
                  <Input
                    type="date"
                    value={formData.expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    required={!isVrcType(formData.document_type)}
                  />
                </div>
              </div>
              {formData.document_type === 'DRIVER_LICENSE' && (
                <div>
                  <Label>Next Renewal Date</Label>
                  <Input
                    type="date"
                    value={formData.renewal_date || ''}
                    onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                  />
                </div>
              )}
              <div>
                <Label>{editingId ? 'Replace File (optional)' : 'Document File'}</Label>
                <Input
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  required={!editingId}
                />
                <p className="text-xs text-slate-500 mt-1">PDF or image, max 10 MB</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runOcrOnUpload}
                  onChange={(e) => setRunOcrOnUpload(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <Sparkles size={14} className="text-amber-600" />
                Run automatic scan after upload (fill in number and dates when possible)
              </label>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Saving…' : editingId ? 'Save Changes' : 'Upload Document'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

            <Button variant="outline" data-testid="bulk-upload-documents-btn" onClick={() => setBulkDialogOpen(true)}>
              <Upload size={18} className="mr-2" />
              Bulk Upload
            </Button>

            <Dialog open={bulkDialogOpen} onOpenChange={handleBulkDialogOpenChange}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Documents</DialogTitle>
                  <DialogDescription>
                    Upload an Excel metadata sheet plus the matching document files. Each row must map to a vehicle registration or driver license.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkUpload} className="space-y-4">
                  <div>
                    <Label>Country (applies to all rows)</Label>
                    <CountrySelect value={bulkCountry} onValueChange={setBulkCountry} />
                  </div>
                  <div>
                    <Button type="button" variant="outline" className="w-full" onClick={downloadBulkTemplate}>
                      <Download size={16} className="mr-2" />
                      Download sample template (.xlsx)
                    </Button>
                  </div>
                  <div>
                    <Label>Excel metadata file</Label>
                    <Input
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => setBulkMetaFile(e.target.files?.[0] || null)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Document files</Label>
                    <Input
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      multiple
                      onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Filenames in the Excel sheet must match these files exactly.
                    </p>
                  </div>
                  {bulkResult && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
                      <p className="font-medium text-slate-800">
                        Imported {bulkResult.created} · Failed {bulkResult.failed}
                      </p>
                      {bulkResult.errors?.length > 0 && (
                        <ul className="max-h-32 overflow-y-auto text-red-700 space-y-1">
                          {bulkResult.errors.map((err, idx) => (
                            <li key={idx}>
                              {err.row ? `Row ${err.row}` : 'Import'}
                              {err.filename ? ` (${err.filename})` : ''}: {err.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => handleBulkDialogOpenChange(false)}>
                      Close
                    </Button>
                    <Button type="submit" disabled={bulkUploading}>
                      {bulkUploading ? 'Uploading…' : 'Upload & Import'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Sparkles className="text-amber-600 shrink-0" size={20} />
        <div>
          <p className="font-semibold text-amber-900">Automatic document scanning</p>
          <p className="text-sm text-amber-800">
            Upload a file to keep it with the record. Turn on scanning to pull document number and dates from the image when possible.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
        <TabsList className="flex flex-wrap h-auto gap-1 justify-start w-full">
          {DOCUMENT_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="text-xs sm:text-sm">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="fleet-card mb-4 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Search (doc # / entity)</Label>
          <Input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Search…" />
        </div>
        <div>
          <Label className="text-xs">Entity name</Label>
          <Input value={filterEntityName} onChange={(e) => setFilterEntityName(e.target.value)} placeholder="Driver or registration…" />
        </div>
        <div>
          <Label className="text-xs">Sort by</Label>
          <Select value={sortField} onValueChange={setSortField}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="issue_date">Issue Date</SelectItem>
              <SelectItem value="expiry_date">Expiry Date</SelectItem>
              <SelectItem value="renewal_date">Next Renewal Date</SelectItem>
              <SelectItem value="document_number">Document Number</SelectItem>
              <SelectItem value="entity_name">Entity Name</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Order</Label>
          <Select value={sortDir} onValueChange={setSortDir}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="fleet-card table-container">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Entity</th>
              <th>Document #</th>
              <th>Country</th>
              <th>Issue Date</th>
              {activeTab === 'DRIVER_LICENSE' || activeTab === 'ALL' ? <th>Next Renewal</th> : null}
              <th>Expiry Date</th>
              <th>File</th>
              <th>OCR Status</th>
              <th>Valid</th>
              {canEdit && <th className="w-32">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {displayedDocuments.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 11 : 10} className="text-center py-8 text-slate-500">No documents found</td>
              </tr>
            ) : (
              displayedDocuments.map((doc) => {
                const entity = doc.entity_type === 'VEHICLE'
                  ? vehicles.find((v) => v.id === doc.entity_id)
                  : drivers.find((d) => d.id === doc.entity_id);
                const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                const hasFile = !!(doc.s3_key || doc.file_url);
                const hl = rowHighlightProps(doc.id);
                const showRenewal = activeTab === 'DRIVER_LICENSE' || activeTab === 'ALL';
                return (
                  <tr key={doc.id} {...hl} className={hl.className}>
                    <td><span className="status-badge">{getDocumentTypeLabel(doc.document_type)}</span></td>
                    <td className="font-semibold">
                      {doc.entity_type === 'VEHICLE'
                        ? entity?.registration_number
                        : `${entity?.first_name} ${entity?.last_name}`}
                    </td>
                    <td className="text-xs font-mono">{doc.document_number}</td>
                    <td><span className={getCountryBadgeClass(doc.country)}>{getCountryLabel(doc.country)}</span></td>
                    <td>{new Date(doc.issue_date).toLocaleDateString()}</td>
                    {showRenewal && (
                      <td>{doc.renewal_date ? new Date(doc.renewal_date).toLocaleDateString() : '—'}</td>
                    )}
                    <td className={isExpired ? 'text-red-600 font-semibold' : ''}>
                      {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      {hasFile ? (
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleViewDocument(doc)}>
                          <FileText size={14} className="mr-1" />
                          View
                          <ExternalLink size={12} className="ml-1 opacity-60" />
                        </Button>
                      ) : (
                        <span className="text-slate-400 text-sm">—</span>
                      )}
                    </td>
                    <td>
                      {doc.ocr_processed ? (
                        <span className="status-badge active">Processed</span>
                      ) : hasFile ? (
                        <span className="status-badge inactive">Pending</span>
                      ) : (
                        <span className="text-slate-400 text-sm">No file</span>
                      )}
                    </td>
                    <td>
                      {doc.validated ? (
                        <span className="status-badge active">Valid</span>
                      ) : isExpired ? (
                        <span className="status-badge maintenance">Expired</span>
                      ) : (
                        <span className="status-badge inactive">Unvalidated</span>
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex gap-1">
                          {hasFile && !doc.ocr_processed && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Run OCR"
                              disabled={ocrRunningId === doc.id}
                              onClick={() => handleRunOcr(doc)}
                            >
                              <ScanLine size={16} className="text-amber-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(doc)}>
                            <Pencil size={16} />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteTarget(doc)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        title="Delete document?"
        description={deleteTarget ? `Permanently delete ${getDocumentTypeLabel(deleteTarget.document_type)} #${deleteTarget.document_number}? The stored file will also be removed.` : undefined}
      />

      <FilePreviewModal
        open={!!preview}
        onOpenChange={(open) => !open && setPreview(null)}
        url={preview?.url}
        filename={preview?.filename}
        contentType={preview?.contentType}
        title={preview?.title || 'Document preview'}
        authToken={token}
      />
    </div>
  );
};

export default Documents;
